import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Center,
  Collapse,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Skeleton,
  Text,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { ArcButton } from '../../../../common/components/ArcButton';
import { SEHomeCard } from '../../../../common/components/SEHomeCard';
import { SlidingAnimationPageBase } from '../../../../common/components/SlidingAnimationPageBase';
import { trackGuestShareAdded } from '../../../../utils/common/analytics';
import { trackDynatraceGuestShareAdded } from '../../../../utils/native/dynatrace-events';
import { useCreateInvitationMutation, useGetInvitationsQuery } from '../../../site/invitationsApi';
import { selectSite } from '../../../site/siteSlice';
import { useGetSiteUsersQuery } from '../../../site/siteUsersApi';
import { getAvatarColor, getAvatarInitial } from './family-member-avatar';
import { FamilyMemberCard } from './FamilyMemberCard';

export function FamilyMembers() {
  const { data: users, isLoading: isSiteUsersLoading } = useGetSiteUsersQuery();
  const { data: invitations, isLoading: isInvitationsLoading } = useGetInvitationsQuery();
  const [isAdding, setIsAdding] = useState(false);
  // Bumped every time the form is opened so it remounts with a blank field. Without this, reopening the form
  // right after a submit reuses the still-mounting instance (the Collapse exit animation hasn't finished
  // unmounting it yet), leaving the previous, failed email in the input.
  const [addFormKey, setAddFormKey] = useState(0);
  const isLoaded = !isSiteUsersLoading && !isInvitationsLoading;
  const { t } = useTranslation();

  const openAddForm = () => {
    setAddFormKey((key) => key + 1);
    setIsAdding(true);
  };

  return (
    <SlidingAnimationPageBase title={t('Account.family members')} backURL={`../home?direction=back`}>
      <Box m={4}>
        <Flex justifyContent="space-between" alignItems="center" mb={2}>
          <Text fontWeight={600}>{t('Account.members added')}</Text>
          <Button
            data-testid="add-new-member-button"
            onClick={openAddForm}
            variant={'ghost'}
            size={'sm'}
            colorScheme={'schneiderSkyBlue'}
            fontWeight={500}
            hidden={isAdding}
          >
            {t('Account.add family member')}
          </Button>
        </Flex>
        {/* Keep the add form above the list so its input + Save button stay on screen no matter how many
            members are present — a long list used to push them below the fold. Expand from zero height so the
            fields grow in rather than pop in. */}
        <Collapse in={isAdding} animateOpacity unmountOnExit>
          <AddNewFamilyMemberForm key={addFormKey} onFinishEdit={() => setIsAdding(false)} />
        </Collapse>

        <Skeleton isLoaded={isLoaded} borderRadius={5} minHeight={12}>
          {users?.map((user, i) => (
            <FamilyMemberCard key={user.user_id} data={user} index={i} />
          ))}

          {invitations?.map((invitation, i) => (
            <PendingInvitationCard
              key={`pending-invitation-${invitation.user_email}-${i}`}
              email={invitation.user_email}
              index={i}
            />
          ))}
        </Skeleton>
      </Box>
    </SlidingAnimationPageBase>
  );
}

// motion(Box) rather than <Box as={motion.div}>: Chakra's `transition` style prop would otherwise swallow
// framer-motion's `transition` object. Also gives correct `children` typing under React 19 + framer-motion 6.
const MotionBox = motion(Box);

function PendingInvitationCard({ email, index }: { email: string; index: number }) {
  const { t } = useTranslation();

  return (
    // Newly-mounted cards (e.g. a just-added invitation) slide down and fade in rather than popping in.
    // Existing cards keep their key across refetches, so they don't re-animate.
    <MotionBox initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
      <SEHomeCard mb={3} width="100%" borderTopLeftRadius={5}>
        <Flex alignItems="center" gap={3} data-testid={`pending-invitation-${index}`}>
          <Center
            flexShrink={0}
            w="36px"
            h="36px"
            borderRadius="full"
            background={getAvatarColor(email)}
            color="white"
            fontWeight={600}
            opacity={0.6}
          >
            {getAvatarInitial(email)}
          </Center>
          <Text isTruncated>{email}</Text>
          <Badge ml="auto" data-testid={`pending-badge-${index}`}>
            {t('Common.pending')}
          </Badge>
        </Flex>
      </SEHomeCard>
    </MotionBox>
  );
}

type AddFamilyMemberFormData = {
  email: string;
};

type AddNewFamilyMemberFormProps = {
  onFinishEdit: () => void;
};

function AddNewFamilyMemberForm({ onFinishEdit }: AddNewFamilyMemberFormProps) {
  const { site_id: siteId } = useSelector(selectSite);
  const { refetch: refetchInvitations } = useGetInvitationsQuery();
  const { refetch: refetchSiteUsers } = useGetSiteUsersQuery();
  const toast = useToast({ isClosable: true });
  const [createInvitation, { isLoading }] = useCreateInvitationMutation();
  const { t } = useTranslation();
  const schema = yup.object({
    email: yup.string().max(100).email().trim().required().label(t('Common.email')),
  });
  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = useForm<AddFamilyMemberFormData>({
    resolver: yupResolver(schema),
  });

  async function handleSubmit(values: AddFamilyMemberFormData) {
    try {
      await createInvitation({ siteId, ...values }).unwrap();
      // Refetch both lists so the new entry shows immediately: invitees without an account become a pending
      // invitation, but invitees who already have a Schneider Charge account are accepted right away and land
      // in the site-users list instead — which the invitation mutation doesn't invalidate.
      await Promise.all([refetchInvitations().unwrap(), refetchSiteUsers().unwrap()]);
      trackGuestShareAdded();
      trackDynatraceGuestShareAdded();
    } catch {
      toast({
        status: 'error',
        title: t('Account.error inviting family member'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
      });
    }

    onFinishEdit();
  }

  return (
    <Box data-testid="add-new-family-member-form" mb={6}>
      <FormControl isInvalid={!!errors?.email} my={2} mb={6}>
        <Input
          borderRadius={0}
          _dark={{ background: 'customGrey.900' }}
          size="md"
          data-testid="email-input"
          placeholder={`${t('Common.email')}...`}
          {...register(`email`)}
        />
        <FormErrorMessage data-testid="email-error">{errors?.email?.message}</FormErrorMessage>
      </FormControl>
      <Center>
        <ArcButton
          arcColor="#3DCD57"
          onClick={handleFormSubmit(handleSubmit)}
          width="90%"
          isLoading={isLoading}
          data-testid="save-button"
        >
          {t('Common.save')}
        </ArcButton>
      </Center>
    </Box>
  );
}
